import { useState } from 'react';
import DashboardLayout from '../components/DashboardLayout';
import { api } from '../lib/api';
import { useAuthStore } from '../lib/store';
import { Sparkles, Mail, Instagram, Facebook, Video, Megaphone, Users } from 'lucide-react';

type GeneratorType = 'facebook-ad' | 'newsletter' | 'instagram-ad' | 'ugc' | 'tiktok-video' | 'ad' | 'influencer';

export default function Generators() {
  const { token } = useAuthStore();
  const [selectedGenerator, setSelectedGenerator] = useState<GeneratorType | null>(null);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<any>(null);

  if (token) {
    api.setToken(token);
  }

  const generators = [
    {
      id: 'facebook-ad' as GeneratorType,
      name: 'Facebook Ads Maker',
      icon: <Facebook className="w-6 h-6" />,
      color: 'from-blue-500 to-blue-600',
      description: 'Create high-converting Facebook ads',
    },
    {
      id: 'newsletter' as GeneratorType,
      name: 'AI Newsletter Generator',
      icon: <Mail className="w-6 h-6" />,
      color: 'from-purple-500 to-purple-600',
      description: 'Generate engaging email newsletters',
    },
    {
      id: 'instagram-ad' as GeneratorType,
      name: 'Instagram Ads Maker',
      icon: <Instagram className="w-6 h-6" />,
      color: 'from-pink-500 to-pink-600',
      description: 'Design eye-catching Instagram ads',
    },
    {
      id: 'ugc' as GeneratorType,
      name: 'AI UGC Generator',
      icon: <Users className="w-6 h-6" />,
      color: 'from-green-500 to-green-600',
      description: 'Create authentic user-generated content scripts',
    },
    {
      id: 'tiktok-video' as GeneratorType,
      name: 'TikTok Video Generator',
      icon: <Video className="w-6 h-6" />,
      color: 'from-cyan-500 to-cyan-600',
      description: 'Generate viral TikTok video scripts',
    },
    {
      id: 'ad' as GeneratorType,
      name: 'Ad Generator',
      icon: <Megaphone className="w-6 h-6" />,
      color: 'from-orange-500 to-orange-600',
      description: 'Create ads for any platform',
    },
    {
      id: 'influencer' as GeneratorType,
      name: 'Influencer Generator',
      icon: <Sparkles className="w-6 h-6" />,
      color: 'from-indigo-500 to-indigo-600',
      description: 'Design influencer marketing campaigns',
    },
  ];

  const handleGenerate = async (formData: any) => {
    setLoading(true);
    setResult(null);

    try {
      let response;
      switch (selectedGenerator) {
        case 'facebook-ad':
          response = await api.generateFacebookAd(formData);
          break;
        case 'newsletter':
          response = await api.generateNewsletter(formData);
          break;
        case 'instagram-ad':
          response = await api.generateInstagramAd(formData);
          break;
        case 'ugc':
          response = await api.generateUGC(formData);
          break;
        case 'tiktok-video':
          response = await api.generateTikTokVideo(formData);
          break;
        case 'ad':
          response = await api.generateAd(formData);
          break;
        case 'influencer':
          response = await api.generateInfluencerCampaign(formData);
          break;
      }
      setResult(response);
    } catch (error: any) {
      console.error('Generation error:', error);
      alert(error.message || 'Failed to generate content');
    } finally {
      setLoading(false);
    }
  };

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">AI Generators</h1>
          <p className="text-gray-600 mt-2">Create professional content with AI in seconds</p>
        </div>

        {!selectedGenerator ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {generators.map((generator) => (
              <button
                key={generator.id}
                onClick={() => setSelectedGenerator(generator.id)}
                className="card hover:shadow-lg transition-shadow text-left"
              >
                <div className={`w-12 h-12 rounded-lg bg-gradient-to-r ${generator.color} text-white flex items-center justify-center mb-4`}>
                  {generator.icon}
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  {generator.name}
                </h3>
                <p className="text-sm text-gray-600">{generator.description}</p>
              </button>
            ))}
          </div>
        ) : (
          <div className="space-y-6">
            <div className="flex items-center gap-4">
              <button
                onClick={() => {
                  setSelectedGenerator(null);
                  setResult(null);
                }}
                className="btn btn-secondary"
              >
                ← Back
              </button>
              <h2 className="text-2xl font-bold text-gray-900">
                {generators.find(g => g.id === selectedGenerator)?.name}
              </h2>
            </div>

            <GeneratorForm
              type={selectedGenerator}
              onSubmit={handleGenerate}
              loading={loading}
            />

            {result && (
              <div className="card bg-gradient-to-br from-blue-50 to-purple-50">
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                  <Sparkles className="w-5 h-5 text-primary-600" />
                  Generated Content
                </h3>
                <div className="space-y-4">
                  <pre className="whitespace-pre-wrap text-sm text-gray-700 bg-white p-4 rounded-lg">
                    {JSON.stringify(result, null, 2)}
                  </pre>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}

function GeneratorForm({
  type,
  onSubmit,
  loading,
}: {
  type: GeneratorType;
  onSubmit: (data: any) => void;
  loading: boolean;
}) {
  const [formData, setFormData] = useState<Record<string, any>>({});

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit(formData);
  };

  const updateField = (field: string, value: any) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  return (
    <form onSubmit={handleSubmit} className="card">
      <div className="space-y-4">
        {type === 'facebook-ad' && (
          <>
            <div>
              <label className="label">Product/Service *</label>
              <input
                type="text"
                className="input"
                placeholder="e.g., Fitness App"
                required
                onChange={(e) => updateField('product', e.target.value)}
              />
            </div>
            <div>
              <label className="label">Target Audience</label>
              <input
                type="text"
                className="input"
                placeholder="e.g., Young professionals"
                onChange={(e) => updateField('target_audience', e.target.value)}
              />
            </div>
            <div>
              <label className="label">Objective</label>
              <select
                className="input"
                onChange={(e) => updateField('objective', e.target.value)}
              >
                <option value="conversions">Conversions</option>
                <option value="awareness">Brand Awareness</option>
                <option value="traffic">Website Traffic</option>
                <option value="engagement">Engagement</option>
              </select>
            </div>
          </>
        )}

        {type === 'newsletter' && (
          <>
            <div>
              <label className="label">Topic *</label>
              <input
                type="text"
                className="input"
                placeholder="e.g., Weekly Marketing Tips"
                required
                onChange={(e) => updateField('topic', e.target.value)}
              />
            </div>
            <div>
              <label className="label">Tone</label>
              <select
                className="input"
                onChange={(e) => updateField('tone', e.target.value)}
              >
                <option value="professional">Professional</option>
                <option value="friendly">Friendly</option>
                <option value="casual">Casual</option>
                <option value="formal">Formal</option>
              </select>
            </div>
            <div>
              <label className="label">Number of Sections</label>
              <input
                type="number"
                className="input"
                min="2"
                max="5"
                defaultValue="3"
                onChange={(e) => updateField('sections', parseInt(e.target.value))}
              />
            </div>
          </>
        )}

        {type === 'instagram-ad' && (
          <>
            <div>
              <label className="label">Product *</label>
              <input
                type="text"
                className="input"
                placeholder="e.g., Summer Collection"
                required
                onChange={(e) => updateField('product', e.target.value)}
              />
            </div>
            <div>
              <label className="label">Style</label>
              <input
                type="text"
                className="input"
                placeholder="e.g., Minimalist, Vibrant"
                onChange={(e) => updateField('style', e.target.value)}
              />
            </div>
            <div>
              <label className="label">Target Audience</label>
              <input
                type="text"
                className="input"
                placeholder="e.g., Fashion-forward millennials"
                onChange={(e) => updateField('target_audience', e.target.value)}
              />
            </div>
          </>
        )}

        {type === 'ugc' && (
          <>
            <div>
              <label className="label">Product *</label>
              <input
                type="text"
                className="input"
                placeholder="e.g., Skincare Product"
                required
                onChange={(e) => updateField('product', e.target.value)}
              />
            </div>
            <div>
              <label className="label">UGC Type</label>
              <select
                className="input"
                onChange={(e) => updateField('ugc_type', e.target.value)}
              >
                <option value="review">Review/Testimonial</option>
                <option value="tutorial">How-To/Tutorial</option>
                <option value="unboxing">Unboxing</option>
                <option value="before-after">Before/After</option>
              </select>
            </div>
            <div>
              <label className="label">Persona</label>
              <input
                type="text"
                className="input"
                placeholder="e.g., Busy mom"
                onChange={(e) => updateField('persona', e.target.value)}
              />
            </div>
          </>
        )}

        {type === 'tiktok-video' && (
          <>
            <div>
              <label className="label">Topic *</label>
              <input
                type="text"
                className="input"
                placeholder="e.g., Morning Routine Hacks"
                required
                onChange={(e) => updateField('topic', e.target.value)}
              />
            </div>
            <div>
              <label className="label">Duration (seconds)</label>
              <input
                type="number"
                className="input"
                min="15"
                max="60"
                defaultValue="30"
                onChange={(e) => updateField('duration', parseInt(e.target.value))}
              />
            </div>
            <div>
              <label className="label">Trend Style</label>
              <select
                className="input"
                onChange={(e) => updateField('trend', e.target.value)}
              >
                <option value="educational">Educational</option>
                <option value="entertainment">Entertainment</option>
                <option value="challenge">Challenge</option>
                <option value="storytime">Storytime</option>
              </select>
            </div>
          </>
        )}

        {type === 'ad' && (
          <>
            <div>
              <label className="label">Product/Service *</label>
              <input
                type="text"
                className="input"
                placeholder="e.g., Online Course"
                required
                onChange={(e) => updateField('product', e.target.value)}
              />
            </div>
            <div>
              <label className="label">Platform</label>
              <select
                className="input"
                onChange={(e) => updateField('platform', e.target.value)}
              >
                <option value="multi-platform">Multi-Platform</option>
                <option value="google">Google Ads</option>
                <option value="facebook">Facebook/Instagram</option>
                <option value="linkedin">LinkedIn</option>
                <option value="twitter">Twitter</option>
              </select>
            </div>
            <div>
              <label className="label">Format</label>
              <select
                className="input"
                onChange={(e) => updateField('format', e.target.value)}
              >
                <option value="image">Image + Text</option>
                <option value="video">Video</option>
                <option value="carousel">Carousel</option>
                <option value="text">Text Only</option>
              </select>
            </div>
          </>
        )}

        {type === 'influencer' && (
          <>
            <div>
              <label className="label">Product/Brand *</label>
              <input
                type="text"
                className="input"
                placeholder="e.g., Sustainable Fashion Brand"
                required
                onChange={(e) => updateField('product', e.target.value)}
              />
            </div>
            <div>
              <label className="label">Niche</label>
              <input
                type="text"
                className="input"
                placeholder="e.g., Eco-friendly lifestyle"
                onChange={(e) => updateField('niche', e.target.value)}
              />
            </div>
            <div>
              <label className="label">Budget Range</label>
              <select
                className="input"
                onChange={(e) => updateField('budget_range', e.target.value)}
              >
                <option value="micro">Micro ($1k-5k)</option>
                <option value="mid">Mid-range ($5k-20k)</option>
                <option value="high">High ($20k+)</option>
              </select>
            </div>
          </>
        )}

        <button
          type="submit"
          disabled={loading}
          className="btn btn-primary w-full flex items-center justify-center gap-2"
        >
          {loading ? (
            <>
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
              Generating...
            </>
          ) : (
            <>
              <Sparkles className="w-4 h-4" />
              Generate with AI
            </>
          )}
        </button>
      </div>
    </form>
  );
}
